<template>
    <div class="modal-full">
        <div class="modal-header">
            <close-icon v-on:click="closeView"></close-icon>
            <h1>Manage User</h1>
        </div>

        <div class="modal-body no-pad" v-if="user">
            <div class="flex h-full">
                <div class="account-sidebar h-full">
                    <div class="mb-3">
                        <label>User ID</label>
                        <p>{{ user.id }}</p>
                    </div>

                    <div class="mb-4" v-if="user.account">
                        <label>Account</label>
                        <p><a href="#" @click.prevent="handleViewAccount">{{ user.account_id }}</a></p>

                        <label>Container</label>
                        <p>{{ user.account ? user.account.container.name : 'N/A' }}</p>
                    </div>

                    <div class="mb-4">
                        <label>User Created</label>
                        <p><abbr :title="formatDateLong(user.created)">{{ formatDateRelative(user.created) }}</abbr></p>

                        <label>Last Login</label>
                        <p><abbr :title="formatDateLong(user.last_login)">{{
                                formatDateRelative(user.last_login)
                            }}</abbr></p>
                    </div>

                    <div class="mb-4" v-if="!userIsAdmin">
                        <p><a :href="activeCampaignUrl" target="_blank">Search in ActiveCampaign</a></p>
                    </div>

                    <div class="mb-4">
                        <p>
                            <router-link
                                :to="{name: 'users.activity', params: {userId: this.user.id}}">
                                View Recent Activity
                            </router-link>
                        </p>
                    </div>

                    <div class="mb-4">
                        <label>Product Licences</label>
                        <p>
                            <router-link
                                :to="{name: 'users.productlicences', params: { userId: this.user.id }}">
                                Manage
                            </router-link>
                        </p>
                    </div>
                </div>

                <div class="account-body">
                    <form class="profile-form">
                        <div class="inline-error" v-if="user.banned">
                            <p>User is suspended</p>
                        </div>

                        <div class="inline-error" v-if="user.blocked">
                            <p>User is on our block-list</p>
                        </div>

                        <div class="inline-warning" v-if="!user.activated">
                            <p>User has not yet confirmed their email address <a href="#"
                                                                                 @click.prevent="handleValidateEmail"
                                                                                 class="ml-1 text-sm text-orange-700">Verify
                                Manually</a></p>
                        </div>

                        <div class="inline-warning" v-if="user.unsubscribes.length > 0">
                            <p>User has unsubscribed from all mailings</p>
                        </div>

                        <div class="md:flex flex-wrap mb-4">
                            <div class="md:w-1/2 pr-2">
                                <label class="block text-gray-800 text-sm mb-2">First Name</label>
                                <input type="text" v-model="profile.firstname" name="firstname"
                                       class="appearance-none border w-full py-2 px-3 text-gray-800 leading-tight focus:outline-none focus:shadow-outline"/>
                            </div>

                            <div class="md:w-1/2 pl-2">
                                <label class="block text-gray-800 text-sm mb-2">Last Name</label>
                                <input type="text" v-model="profile.lastname" name="lastname"
                                       class="appearance-none border w-full py-2 px-3 text-gray-800 leading-tight focus:outline-none focus:shadow-outline"/>
                            </div>
                        </div>

                        <div class="md:flex flex-wrap mb-4">
                            <div class="md:w-1/2 pr-2">
                                <label class="block text-gray-800 text-sm mb-2">Email Address</label>
                                <input type="text" v-model="user.email"
                                       class="appearance-none border w-full py-2 px-3 text-gray-800 leading-tight focus:outline-none focus:shadow-outline"/>
                            </div>
                        </div>

                        <div class="md:flex flex-wrap mb-4">
                            <div class="md:w-1/2 pr-2">
                                <label class="block text-gray-800 text-sm mb-2">Telephone</label>
                                <input type="text" v-model="profile.telephone" name="telephone"
                                       class="appearance-none border w-full py-2 px-3 text-gray-800 leading-tight focus:outline-none focus:shadow-outline"/>
                            </div>
                        </div>

                        <div class="md:flex flex-wrap mb-4" v-if="userIsJournalist">
                            <div class="md:w-1/2 pr-2">
                                <label class="block text-gray-800 text-sm mb-2">Twitter Handle</label>
                                <input type="text" v-model="profile.twitter_handle"
                                       class="appearance-none border w-full py-2 px-3 text-gray-800 leading-tight focus:outline-none focus:shadow-outline"/>
                            </div>
                        </div>

                        <div class="journalist-profile" v-if="userIsJournalist">
                            <div class="md:flex flex-wrap mb-4">
                                <div class="md:w-1/2 pr-2">
                                    <label class="block text-gray-800 text-sm mb-2">Publication</label>
                                    <v-select
                                        label="label"
                                        v-model="user.publication_id"
                                        :options="publications"
                                        :reduce="publication => publication.id"
                                        v-if="publications" />
                                </div>
                                <div class="md:w-1/2">
                                    <label class="block text-gray-800 text-sm mb-2">&nbsp;</label>
                                    <button type="button" @click="hasNewPublication = !hasNewPublication"
                                            :class="[hasNewPublication ? 'bg-gray-300 hover:bg-gray-300 focus:bg-gray-300' : '']"
                                            class="inline-flex items-center px-3 pt-2 pb-1 border shadow-sm text-sm leading-4 font-medium rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 border-gray-300 text-gray-700 bg-white">
                                        <PlusIcon class="h-5 w-5 mr-2" />
                                        New Publication
                                    </button>
                                </div>
                                <div class="md:flex flex-wrap mb-4" v-if="hasNewPublication">
                                    <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                                        <div class="sm:col-span-3">
                                            <label for="pubName" class="block text-sm font-medium text-gray-700"> Publication Name </label>
                                            <div class="mt-1">
                                                <input type="text" name="pubName" id="pubName" autocomplete="given-name" class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" v-model="newPublication.title" />
                                            </div>
                                        </div>

                                        <div class="sm:col-span-3">
                                            <label for="pubType" class="block text-sm font-medium text-gray-700"> Publication Type</label>
                                            <div class="mt-1">
                                                <select id="pubType" name="pubType" class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" v-model="newPublication.type">
                                                    <option v-for="mediaType in mediaTypes" :value="mediaType.value">
                                                        {{ mediaType.text }}
                                                    </option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="md:flex flex-wrap mb-4">
                                <div class="md:w-1/2 pr-2">
                                    <label class="block text-gray-800 text-sm mb-2">Role</label>
                                    <input type="text" v-model="profile.role"
                                           class="appearance-none border w-full py-2 px-3 text-gray-800 leading-tight focus:outline-none focus:shadow-outline"/>
                                </div>
                            </div>
                        </div>

                        <div v-if="userIsJournalist">
                            <p class="text-lg font-light my-6">Interests</p>

                            <div class="mb-3">
                                <label class="block text-gray-800 text-sm mb-2">Sectors</label>

                                <picker-selector
                                    v-if="sectors && sectors.length > 0"
                                    placeholder="Select Sectors"
                                    v-model="profileSectors"
                                    input-name="sectors[]"
                                    @update:modelValue="addSectors"
                                    :options="sectors">
                                </picker-selector>
                            </div>

                            <div class="mb-3">
                                <label class="block text-gray-800 text-sm mb-2">Locations</label>

                                <picker-selector
                                    v-if="locations && locations.length > 0"
                                    placeholder="Select Locations"
                                    v-model="profileLocations"
                                    input-name="locations[]"
                                    @update:modelValue="addLocations"
                                    :options="locations">
                                </picker-selector>
                            </div>
                        </div>

                        <div v-if="userIsJournalist">
                            <p class="text-lg font-light my-6">Mailing Preferences</p>

                            <div class="mb-3">
                                <div class="md:flex">
                                    <div class="md:w-1/4">
                                        <div class="flex items-center mb-2">
                                            <CheckBox v-model="profile.mailing_pressrelease" val="yes"/>
                                            <label class="inline-block text-gray-800 text-sm ml-2">Press Release
                                                Notifications</label>
                                        </div>
                                    </div>

                                    <div class="md:w-1/4">
                                        <div class="flex items-center mb-2">
                                            <CheckBox v-model="profile.mailing_updates" val="yes"/>
                                            <label class="inline-block text-gray-800 text-sm ml-2">News &amp;
                                                Updates</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-if="userIsBusiness">
                            <p class="text-lg font-light my-6">Mailing Preferences</p>

                            <div class="mb-3">
                                <div class="md:flex mb-3">
                                    <div class="md:w-1/4">
                                        <div class="flex items-center mb-2">
                                            <CheckBox v-model="profile.mailing_mediarequests" val="yes"/>
                                            <label class="inline-block text-gray-800 text-sm ml-2">Media Request
                                                Notifications</label>
                                        </div>
                                    </div>

                                    <div class="md:w-1/4">
                                        <div class="flex items-center mb-2">
                                            <CheckBox v-model="profile.mailing_pressrelease" val="yes"/>
                                            <label class="inline-block text-gray-800 text-sm ml-2">Media Request
                                                Receipts</label>
                                        </div>
                                    </div>

                                    <div class="md:w-1/4">
                                        <div class="flex items-center mb-2">
                                            <CheckBox v-model="profile.mailing_updates" val="yes"/>
                                            <label class="inline-block text-gray-800 text-sm ml-2">Account Activity
                                                Notifications</label>
                                        </div>
                                    </div>

                                    <div class="md:w-1/4">
                                        <div class="flex items-center mb-2">
                                            <CheckBox v-model="profile.mailing_newsletter" val="yes"/>
                                            <label class="inline-block text-gray-800 text-sm ml-2">Newsletter</label>
                                        </div>
                                    </div>
                                </div>

                                <div class="md:flex">
                                    <div class="md:w-1/4">
                                        <div class="flex items-center mb-2">
                                            <CheckBox v-model="profile.mailing_updates" val="yes"/>
                                            <label class="inline-block text-gray-800 text-sm ml-2">Updates</label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="md:w-1/2 pr-2">
                                <label class="block text-gray-800 text-sm mb-2">Email alias</label>
                                <textarea v-model.trim="profile.email_alias" name="email_alias"
                                          class="appearance-none border w-full py-2 px-3 text-gray-800 leading-tight focus:outline-none focus:shadow-outline"/>
                                <i class="block text-gray-800 text-sm mb-2">Separated by comma</i>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <div class="modal-footer" v-if="user">
            <div class="flex">
                <div class="left text-left">
                    <div class="actions" v-click-away="hideActions">
                        <div class="actions-menu" v-show="actionsShown">
                            <template v-if="!userIsAdmin">
                                <button class="btn-primary muted" type="button" @click="handleViewAccount"
                                        v-if="user.account">View Account
                                </button>
                                <button class="btn-tertiary" type="button" @click="handleMasquerade">
                                    Masquerade As User
                                </button>
                            </template>
                            <button class="btn-primary muted" type="button" @click="handleResetPassword">Reset
                                Password
                            </button>
                            <button class="btn-primary muted" type="button" @click="handleSendActivation"
                                    v-if="!user.activated">Resend Activation Email
                            </button>
                            <button class="btn-tertiary" type="button" @click="handleViewPermissions"
                                    v-if="userIsAdmin">Permissions
                            </button>
                            <button class="btn-danger muted" type="button" @click="addToBlocklist" v-if="!user.blocked">
                                Add User to 'Do Not Send' list
                            </button>
                            <button class="btn-danger muted" type="button" @click="toggleSuspensionStatus" v-if="user">
                                {{ isUserBanned }}
                            </button>
                            <button class="btn-danger muted" type="button" @click="handleDeleteUser">Delete User
                            </button>
                        </div>
                        <button class="btn-primary muted" type="button" :data-open="actionsShown"
                                @click="actionsShown = !actionsShown">
                            <ChevronUpIcon class="h-6 w-6 inline" />
                            Actions
                        </button>
                    </div>
                </div>
                <div class="right text-right">
                    <button class="btn-tertiary muted" type="button" @click="closeView">Close</button>
                    <button class="btn-primary" type="button" @click="saveForm">Save</button>
                </div>
            </div>
        </div>
        <router-view v-slot="{ Component }">
            <transition name="drop-in" mode="in-out">
                <component :is="Component"></component>
            </transition>
        </router-view>
    </div>
</template>

<script>
import {ChevronUpIcon, PlusIcon} from "@heroicons/vue/24/solid"
import { useStore } from "vuex"
import "vue-select/dist/vue-select.css";

export default {
    name: 'UserView',
    data() {
        return {
            user: null,
            mediaTypes: {},
            editFields: {
                email: false,
            },
            profileSectors: null,
            profileLocations: null,
            profile: {
                name: null,
                firstname: null,
                lastname: null,
                telephone: null,
                media_type: 0,
                publication: null,
                role: null,
                twitter_handle: null,
                mailing_pressrelease: false,
                mailing_mediarequests: false,
                mailing_mediarequestreceipts: false,
                mailing_newsletter: false,
                mailing_updates: false,
                email_alias: null
            },
            hasNewPublication: false,
            newPublication: {
                title: null,
                type: null,
            },
            publications: [],
            actionsShown: false,
            loading: false
        }
    },
    components: {
        ChevronUpIcon,
        PlusIcon,
    },
    watch: {
        editFields: {
            handler(object) {
                Object.keys(object).forEach(function (key) {
                    if (object[key] === false) {
                        document.getElementsByClassName('edit-field ' + key).item(0).previousElementSibling.classList.remove('hide')
                        document.getElementsByClassName('edit-field ' + key).item(0).classList.remove('show')
                    } else {
                        document.getElementsByClassName('edit-field ' + key).item(0).previousElementSibling.classList.add('hide')
                        document.getElementsByClassName('edit-field ' + key).item(0).classList.add('show')
                    }
                })
            },
            deep: true,
        }
    },
    computed: {
        masqueradeLink: function () {
            return route('users.masquerade', this.$route.params.userId)
        },
        isUserBanned: function () {
            return this.user.banned ? 'Remove Suspension' : 'Suspend User'
        },
        /**
         * Return the link to search the contact in ActiveCampaign
         *
         * @return {String}
         */
        activeCampaignUrl() {
            return 'https://journolink.activehosted.com/app/contacts?q=' + this.user.email
        },
        /**
         * Determine if the user a Business account
         *
         * @return {Boolean}
         */
        userIsBusiness() {
            return this.user.usertype === 'business'
        },
        /**
         * Determine if the user a Business account
         *
         * @return {Boolean}
         */
        userIsJournalist() {
            return this.user.usertype === 'journalist'
        },
        /**
         * Determine if the user a Admin account
         *
         * @return {Boolean}
         */
        userIsAdmin() {
            return this.user.usertype === 'admin'
        },

        sectors () {
            const store = useStore()
            return store.state.sectors ?? []
        },

        locations () {
            const store = useStore()
            return store.state.locations ?? []
        },
    },
    mounted() {
        this.loadData()

        this.setMetaTitle('View User | JournoLink Control')

        window.addEventListener('keyup', this.handleKeypress)
    },
    beforeDestroy() {
        window.removeEventListener('keyup', this.handleKeypress)
    },
    beforeRouteUpdate(to, from, next) {
        this.$route.params.userId = to.params.userId
        this.loadData()
        next()
    },
    methods: {
        loadData() {
            this.loading = true
            axios.get(route('users.show', this.$route.params.userId))
                .then(response => {
                    this.fillData(response.data)

                    this.setMetaTitle(this.user.email + ' | JournoLink Control')

                    this.loading = false
                })
                .catch(e => {
                    console.error({e})
                })
        },
        /**
         * Fill local data from API response
         *
         * @param {Object}
         * @return {void}
         */
        fillData(data) {
            this.user = data.user
            this.profile = this.user.profile
            this.profileSectors = _.get(this.profile, 'sector')
            this.profileLocations = _.get(this.profile, 'location')

            if (this.user.usertype === 'journalist') {
                axios.get(route('publications.selectList'))
                    .then(response => {
                        this.publications = response.data.results
                        this.mediaTypes = response.data.mediaTypes

                        this.loading = false
                    })
                    .catch(e => {
                        console.error({e})
                    })
            }
        },
        /**
         * Emit the close event to the parent
         *
         * @return {void}
         */
        closeView() {
            this.setMetaTitle('List Users | JournoLink Control')
            this.$root.goBack()
        },
        /**
         *
         */
        endEditing: function () {
            var fields = this.editFields
            Object.keys(fields).forEach(function (key) {
                fields[key] = false
            })
            this.editFields = fields
        },
        /**
         *
         */
        saveForm() {
            this.processEmailAlias()

            this.profile.location = this.profileLocations
            this.profile.sector = this.profileSectors

            axios.post(route('users.save', this.$route.params.userId), {
                user: this.user,
                profile: this.profile,
                newPublication: this.newPublication,
            })
                .then(response => {
                    this.fillData(response.data)
                    this.$bus.$emit('alert.success', 'Update Success')
                    this.setMetaTitle(this.user.email + ' | JournoLink Control')
                    this.hasNewPublication = false
                    this.newPublication = {
                        title: null,
                        type: null,
                    }

                    this.loadData()
                })
                .catch(e => {
                    this.$bus.$emit('alert.dander', 'Update Failed')
                    console.error({e})
                })

            this.endEditing()
        },
        /**
         * Convert Email Alias to array
         *
         * @return {void}
         */
        processEmailAlias() {
            if (typeof this.profile.email_alias == 'string') {
                let email_alias = this.profile.email_alias.replace(" ", "").split(',')
                let temp_email_alias = []

                let re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                email_alias.forEach(function (item) {
                    if (re.test(item)) {
                        temp_email_alias.push(item)
                    }
                })

                this.profile.email_alias = temp_email_alias
            }
        },
        /**
         * Handle a keypress event
         *
         * @return {void}
         */
        handleKeypress(event) {
            //
        },
        /**
         * Return the local user name from an email address
         *
         * @param  {String} email
         * @return {String}
         */
        getUserFromEmail(email) {
            return email.substring(0, email.lastIndexOf("@"))
        },
        /**
         * Handle a suspend request event
         *
         * @return {void}
         */
        toggleSuspensionStatus() {
            let text = this.user.banned ? 'This will remove suspension on the users account' : 'This will suspend the users account and they will not be able to login'
            this.alert.fire({
                title: 'Are you sure?',
                text: text,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, continue',
                confirmButtonColor: '#E3342F',
                cancelButtonText: 'No, go back'
            })
                .then((selected) => {
                    if (selected.value) {
                        axios.post(route('users.suspend', this.$route.params.userId), {}).then(response => {
                            if (response.data.success === true) {
                                this.$bus.$emit('alert.success', 'Suspension update success')
                                this.user.banned = !this.user.banned
                            } else {
                                this.$bus.$emit('alert.danger', response.data.message)
                            }
                        }).catch(e => {
                            this.$bus.$emit('alert.danger', 'Suspension update failed')
                        })
                    }
                })
        },
        addToBlocklist() {
            this.alert.fire({
                title: 'Are you sure?',
                text: 'This will add the user to the "Do Not Send" list',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, continue',
                confirmButtonColor: '#E3342F',
                cancelButtonText: 'No, go back'
            })
                .then((selected) => {
                    if (selected.value) {
                        axios.post(route('users.block', this.$route.params.userId), {}).then(response => {
                            if (response.data.success === true) {
                                this.$bus.$emit('alert.success', 'Success')
                                this.user.blocked = true
                            } else {
                                this.$bus.$emit('alert.danger', response.data.message)
                            }
                        }).catch(e => {
                            this.$bus.$emit('alert.danger', 'Block failed')
                        })
                    }
                })
        },
        /**
         * View Account associated with this user
         */
        handleViewAccount() {
            this.$router.push({name: 'accounts.show', params: {accountId: this.user.account_id}})
        },
        /**
         * View Account associated with this user
         */
        handleViewPermissions() {
            this.$router.push({name: 'users.admin.permissions', params: {accountId: this.user.account_id}})
        },
        /**
         /**
         *
         */
        toggleForm(className) {
            this.editFields[className] = this.editFields[className] === false
        },
        /**
         *
         */
        handleResetPassword() {
            this.alert.fire({
                title: 'Confirm Reset Password',
                text: 'Confirm you wish to reset the user\'s account password. This will send an email to the user containing a link to continue the process',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Yes, continue',
                confirmButtonColor: '#2980b9',
                cancelButtonText: 'No, go back'
            })
                .then((selected) => {
                    if (selected.value) {
                        axios.post(route('users.reset-password', this.$route.params.userId), this.user)
                            .then(response => {
                                this.$bus.$emit('alert.success', 'Reset password email sent')
                            })
                            .catch(e => {
                                this.$bus.$emit('alert.danger', 'Reset password email sending failed')
                            })
                    }
                })
        },
        handleSendActivation() {
            this.alert.fire({
                title: 'Confirm Send Activation Email',
                text: `Confirm you wish to send the activation email to account ${this.user.email}.`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Yes, continue',
                confirmButtonColor: '#2980b9',
                cancelButtonText: 'No, go back'
            })
                .then((selected) => {
                    if (selected.value) {
                        axios.post(route('users.send-activation', this.$route.params.userId))
                            .then(response => {
                                this.$bus.$emit('alert.success', 'Email sent successfully')
                            })
                            .catch(e => {
                                this.$bus.$emit('alert.danger', 'Email sending failed')
                            })
                    }
                })
        },
        /**
         * Handle an action to manually validate the User email address
         *
         * @return {void}
         */
        handleValidateEmail() {
            this.alert.fire({
                title: 'Manually Validate Email',
                text: `Confirm you wish to manually confirm the email address for ${this.user.email}.`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Yes, continue',
                confirmButtonColor: '#2980b9',
                cancelButtonText: 'No, go back'
            })
                .then((selected) => {
                    if (selected.value) {
                        axios.post(route('users.save', this.$route.params.userId), {
                            user: {
                                activated: 1
                            }
                        })
                            .then(response => {
                                this.fillData(response.data)
                                this.$bus.$emit('alert.success', 'User has been validated')
                            })
                            .catch(e => {
                                this.$bus.$emit('alert.danger', 'Update failed')
                            })
                    }
                })
        },
        addLocations(ids) {
            this.profileLocations = ids
        },
        addSectors(ids) {
            this.profileSectors = ids
        },
        /**
         * Handle an [Escape] keypress
         *
         * @return {void}
         */
        onEscapeKeypress() {
            this.closeView()
        },
        /**
         * Hide the actions box
         */
        hideActions() {
            this.actionsShown = false
        },
        /**
         * delete the user
         */
        handleDeleteUser() {
            let text = (this.user.account && this.user.account.users_count === 1) ?
                'This is the last remaining user on the account, by continuing both the user and the account will be permanently deleted' :
                'This will permanently delete the user'

            this.alert.fire({
                title: 'Confirm Delete User',
                text: text,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Yes, continue',
                confirmButtonColor: '#2980b9',
                cancelButtonText: 'No, go back'
            })
                .then((selected) => {
                    if (selected.value) {
                        axios.delete(route('users.delete', this.$route.params.userId))
                            .then(response => {
                                if (response.data.success === true) {
                                    this.$bus.$emit('alert.success', 'User delete success')
                                    this.closeView()
                                } else {
                                    this.$bus.$emit('alert.danger', response.data.message)
                                }
                            })
                            .catch(e => {
                                this.$bus.$emit('alert.danger', 'User delete failed')
                            })
                    }
                })
        },
        /**
         * Masquerade as user
         */
        handleMasquerade() {
            let masqueradeLink = route('users.masquerade', this.$route.params.userId)
            let redirect = window.open(masqueradeLink, '_blank')
            redirect.location
        }
    },
}
</script>

<style lang="less">
.account-sidebar {
    padding: 15px;
    width: 350px;
    font-size: 0.9rem;
    background: #f6f6f6;

    label {
        font-weight: bold;
        font-size: 0.8em;
        color: #999999;
    }

    p {
        margin-bottom: 0.5em;
    }
}

.account-activity {
    width: 250px;
    font-size: 0.9rem;
    padding: 15px;
}

.hidden-input {
    input {
        display: none;
        width: 100%;
    }

    .show {
        display: inline-block;
    }

    .hide {
        display: none;
    }

    .edit-field-link {
        text-decoration-line: underline;
        text-decoration-style: solid;
        color: #222222;
        word-wrap: break-word;
    }
}

.profile-form {
    .section-title {
        margin-top: 20px;
    }

    .form-title {
        margin-bottom: 20px;
    }

    .journalist-profile {
        .publication-select {
            display: flex;
            align-items: flex-end;

            .btn-primary {
                width: 100%;
            }
        }

        .fade-enter-active {
            transition: all .3s;
            opacity: 1;
        }

        .fade-enter {
            transition: all .3s;
            opacity: 0;
        }

        .fade-leave-active {
            transition: all .3s;
            opacity: 0;
        }
    }
}

.actions-menu {
    position: fixed;
    left: 0;
    bottom: 72px;
    padding: 15px;
    background: #dde0e2;

    > button {
        margin-bottom: 10px;
        display: block;
        width: 100%;
    }
}
</style>
